import { useState, useEffect } from 'react';

const DEFAULT_DELAY = 500;

/**
 * Custom hook for debouncing a value.
 *
 * @param value - The value to be debounced.
 * @param delay - The delay in milliseconds before the debounced value updates.
 * @returns The debounced value.
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 500);
 *
 * useEffect(() => {
 *  // Make API call
 * }, [debouncedSearchTerm]);
 */

export const useDebounce = (
  value: string,
  delay: number = DEFAULT_DELAY
): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
